import { Controller, useFormContext } from 'react-hook-form';
import { DateRangePicker } from 'shared/mui/DatePicker/DateRangePicker/DateRangePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { AutocompleteInput, AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';

export const useCommissionRecalculationTaskFilterFields = (): FilterFieldConfig[] => {
  const { control, watch, setValue, resetField } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const [merchant, provider] = watch(['merchant', 'provider']);

  return [
    {
      name: 'appliedDate',
      label: 'Период перерасчёта от - до',
      chipType: 'dateTimeRange',
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateRangePicker
              label="Период перерасчёта от - до"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'status',
      label: 'Состояние',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'commission_recalculation_statuses',
        optionText: 'description',
      },
      input: (
        <ReferenceInput perPage={100} reference="commission_recalculation_statuses" source="status">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Состояние"
            optionText="description"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchant',
      label: 'Мерчанты',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchants',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('provider', []);
          resetField('provider', { keepDirty: true });
        },
        onDelete: () => {
          setAppliedFilter('provider', []);
          resetField('provider', { keepDirty: true });
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{ exists: { merchantAccounts: true } }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              onChange: () => {
                setValue('provider', []);
              },
            })}
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Мерчанты"
            name="merchant"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдеры',
      chipType: 'multiselect',
      chipProps: {
        resource: 'providers',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteMultiselectInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Провайдеры"
            name="provider"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'commission',
      label: 'Комиссии',
      chipType: 'multiselect',
      chipProps: {
        resource: 'commissions',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={{
            merchant: merchant,
            provider,
            ignoreParentFilter: true,
          }}
          perPage={100}
          reference="commissions"
          source="commission"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Комиссии"
            name="commission"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
